/**
 * sync-core — the conflict rules behind the co-driven demo's "both can click" mode.
 *
 * Either screen can change anything, so two changes to the same thing can cross on the
 * wire. Every change carries a Stamp, and each screen keeps the NEWEST stamp per key
 * (last writer wins). Once the messages land, both screens hold identical values no
 * matter what order they arrived in. Pure logic — no React, no socket — so it is unit-
 * tested directly (test/sync-core.test.ts, `npm test`).
 */

/** When (hybrid-clock reading) and by whom (per-tab id) a value was written. */
export interface Stamp {
  t: number;
  by: string;
}

/**
 * Hybrid logical clock. A local write reads max(last + 1, wall-clock ms): stamps track
 * real time when the two devices agree, and still order CAUSALLY when they don't —
 * observe() folds in every remote stamp, so a click made after seeing the other
 * person's change is always stamped after it, even on a device whose clock is behind.
 */
export class HybridClock {
  private last = 0;
  private readonly wall: () => number;

  constructor(wall: () => number = () => Date.now()) {
    this.wall = wall;
  }

  tick(): number {
    this.last = Math.max(this.last + 1, this.wall());
    return this.last;
  }

  observe(t: number): void {
    if (t > this.last) this.last = t;
  }
}

/** True when a write stamped `a` should replace one stamped `b`. Later wins; an exact
 *  tie falls to the writer id, so both screens always pick the same winner. */
export function isNewer(a: Stamp | undefined, b: Stamp | undefined): boolean {
  if (!a) return false;
  if (!b) return true;
  if (a.t !== b.t) return a.t > b.t;
  return a.by > b.by;
}

/** Runtime check for a stamp arriving over the wire. */
export function isStamp(x: unknown): x is Stamp {
  return (
    typeof x === "object" &&
    x !== null &&
    typeof (x as Stamp).t === "number" &&
    Number.isFinite((x as Stamp).t) &&
    typeof (x as Stamp).by === "string"
  );
}

/** Content equality for JSON-safe values (a relayed value is always a fresh object). */
export function sameValue(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

const NO_BASELINE = Symbol("no-baseline");

interface Entry {
  value: unknown;
  stamp?: Stamp;
}

/** One last-writer-wins register per key. */
export class LwwMap {
  private readonly entries = new Map<string, Entry>();
  private readonly clock: HybridClock;
  private readonly me: string;

  constructor(clock: HybridClock, me: string) {
    this.clock = clock;
    this.me = me;
  }

  has(key: string): boolean {
    return this.entries.has(key);
  }

  get(key: string): unknown {
    return this.entries.get(key)?.value;
  }

  stampOf(key: string): Stamp | undefined {
    return this.entries.get(key)?.stamp;
  }

  /**
   * A change made on THIS screen. Returns the stamp to broadcast, or null when it
   * changes nothing — the value is already held, or (for a key not yet written in this
   * room) it equals `baseline`, the caller's current value. That null is the echo
   * damper: after the other person's change is applied, a handler that writes the same
   * value again sends nothing back.
   */
  setLocal(key: string, value: unknown, baseline: unknown = NO_BASELINE): Stamp | null {
    const cur = this.entries.get(key);
    const unchanged = cur
      ? sameValue(cur.value, value)
      : baseline !== NO_BASELINE && sameValue(baseline, value);
    if (unchanged) return null;
    const stamp: Stamp = { t: this.clock.tick(), by: this.me };
    this.entries.set(key, { value, stamp });
    return stamp;
  }

  /**
   * A change from the OTHER screen. Kept only if newer than what this screen holds —
   * or when `force`d (the host's join snapshot is authoritative). Returns true when the
   * VALUE changed (re-render); a newer stamp on an equal value is recorded silently.
   * Unstamped changes (an older bundle mid-deploy) always apply.
   */
  applyRemote(key: string, value: unknown, stamp?: Stamp, force = false): boolean {
    if (stamp) this.clock.observe(stamp.t);
    const cur = this.entries.get(key);
    if (!force && stamp && cur?.stamp && !isNewer(stamp, cur.stamp)) return false;
    this.entries.set(key, { value, stamp });
    return !cur || !sameValue(cur.value, value);
  }

  /** Forget one key (it reverts to each screen's own default). */
  delete(key: string): void {
    this.entries.delete(key);
  }

  /** Every value with its stamp — the host's late-join snapshot. */
  snapshot(): [string, unknown, Stamp | undefined][] {
    return Array.from(this.entries, ([k, e]): [string, unknown, Stamp | undefined] => [k, e.value, e.stamp]);
  }

  clear(): void {
    this.entries.clear();
  }
}
